/**
 * Currency context — provides exchange rates, conversion, and formatting.
 * Rates are fetched from a public API and cached in localStorage for 24h.
 * All amounts are stored in EUR and converted client-side for display only.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { getItem, setItem } from '@/shared/lib/localStorage';
import { logger } from '@/shared/lib/logger';

export interface CurrencyOption {
  code: string;
  symbol: string;
  name: string;
  flag: string;
}

export interface CurrencyMeta {
  rate: number;
  name: string;
  symbol: string;
  flag: string;
}

const FALLBACK_CURRENCIES: CurrencyOption[] = [
  { code: 'EUR', symbol: '€', name: 'Euro', flag: '🇪🇺' },
  { code: 'USD', symbol: '$', name: 'US Dollar', flag: '🇺🇸' },
  { code: 'GBP', symbol: '£', name: 'British Pound', flag: '🇬🇧' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc', flag: '🇨🇭' },
  { code: 'TRY', symbol: '₺', name: 'Turkish Lira', flag: '🇹🇷' },
  { code: 'CAD', symbol: 'CA$', name: 'Canadian Dollar', flag: '🇨🇦' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', flag: '🇦🇺' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen', flag: '🇯🇵' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan', flag: '🇨🇳' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee', flag: '🇮🇳' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real', flag: '🇧🇷' },
  { code: 'MXN', symbol: 'MX$', name: 'Mexican Peso', flag: '🇲🇽' },
  { code: 'KRW', symbol: '₩', name: 'South Korean Won', flag: '🇰🇷' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar', flag: '🇸🇬' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham', flag: '🇦🇪' },
  { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal', flag: '🇸🇦' },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone', flag: '🇳🇴' },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krona', flag: '🇸🇪' },
  { code: 'DKK', symbol: 'kr', name: 'Danish Krone', flag: '🇩🇰' },
  { code: 'PLN', symbol: 'zł', name: 'Polish Złoty', flag: '🇵🇱' },
];

const FALLBACK_RATES: Record<string, number> = {
  EUR: 1,
  USD: 1.09,
  GBP: 0.85,
};

const API_URLS = [
  'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/eur.json',
  'https://latest.currency-api.pages.dev/v1/currencies/eur.json',
];
const CACHE_KEY = 'mondivago_exchange_rates_v2';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

interface CachedData {
  rates: Record<string, number>;
  fetchedAt: number;
}

interface CurrencyContextType {
  selectedCurrency: CurrencyOption;
  setCurrency: (code: string) => void;
  rates: Record<string, number>;
  currencies: Record<string, CurrencyMeta>;
  convert: (amount: number, from?: string) => number;
  format: (amount: number, from?: string) => string;
  loading: boolean;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

function getCached(): CachedData | null {
  try {
    const raw = getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedData;
    if (Date.now() - parsed.fetchedAt > CACHE_DURATION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function setCache(rates: Record<string, number>) {
  try {
    setItem(CACHE_KEY, JSON.stringify({ rates, fetchedAt: Date.now() }));
  } catch {
    /* ignore */
  }
}

function ratesToMeta(rates: Record<string, number>): Record<string, CurrencyMeta> {
  const map: Record<string, CurrencyMeta> = {};
  for (const c of FALLBACK_CURRENCIES) {
    map[c.code] = {
      rate: rates[c.code] ?? 1,
      name: c.name,
      symbol: c.symbol,
      flag: c.flag,
    };
  }
  return map;
}

export function CurrencyProvider({ children }: { children: React.ReactNode }) {
  const [currencyCode, setCurrencyCode] = useState<string>(
    () => getItem('mondivago_user_currency') ?? 'EUR'
  );

  const [loading, setLoading] = useState(true);
  const [rates, setRates] = useState<Record<string, number>>(() => {
    const cached = getCached();
    return cached?.rates ?? FALLBACK_RATES;
  });
  const currencies = useMemo(() => ratesToMeta(rates), [rates]);

  useEffect(() => {
    let active = true;

    void (async () => {
      for (const url of API_URLS) {
        try {
          const res = await fetch(url);
          if (!res.ok) continue;
          const data = (await res.json()) as { eur?: Record<string, number> };
          if (!data?.eur) continue;

          const normalized: Record<string, number> = { EUR: 1 };
          for (const [key, val] of Object.entries(data.eur)) {
            normalized[key.toUpperCase()] = Number(val);
          }

          if (!active) return;
          setRates(normalized);
          setCache(normalized);
          setLoading(false);
          return;
        } catch {
          // try next URL
        }
      }
      setLoading(false);
      logger.warn('[Currency] All APIs failed, using fallback rates');
    })();

    return () => {
      active = false;
    };
  }, []);

  const selectedCurrency = useMemo((): CurrencyOption => {
    const meta = currencies[currencyCode];
    if (meta) {
      return { code: currencyCode, name: meta.name, symbol: meta.symbol, flag: meta.flag };
    }
    return FALLBACK_CURRENCIES.find((c) => c.code === currencyCode) ?? FALLBACK_CURRENCIES[0];
  }, [currencies, currencyCode]);

  const setCurrency = useCallback(
    (code: string) => {
      if (code in currencies || FALLBACK_CURRENCIES.some((c) => c.code === code)) {
        setCurrencyCode(code);
        localStorage.setItem('mondivago_user_currency', code);
        window.dispatchEvent(new Event('storage'));
      }
    },
    [currencies]
  );

  const convert = useCallback(
    (amount: number, from = 'EUR'): number => {
      const fromRate = rates[from.toUpperCase()] || 1;
      const toRate = rates[selectedCurrency.code] || 1;
      return (amount / fromRate) * toRate;
    },
    [rates, selectedCurrency.code]
  );

  const format = useCallback(
    (amount: number, from = 'EUR'): string => {
      const converted = convert(amount, from);
      const localeMap: Record<string, string> = {
        EUR: 'en-US',
        USD: 'en-US',
        GBP: 'en-GB',
        CHF: 'de-CH',
        TRY: 'tr-TR',
        CAD: 'en-CA',
        AUD: 'en-AU',
      };
      return new Intl.NumberFormat(localeMap[selectedCurrency.code] || 'en-US', {
        style: 'currency',
        currency: selectedCurrency.code,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(converted);
    },
    [convert, selectedCurrency.code]
  );

  return (
    <CurrencyContext.Provider value={{ selectedCurrency, setCurrency, rates, currencies, convert, format, loading }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}
